import { useSelector } from "react-redux";
import { Route, Routes, Navigate } from "react-router-dom";
import React, { useMemo } from "react";
import { mainModules } from "../generic/artcoded/modulesList";
import { RootState } from "../../store/reducers/rootReducer";
import MiunMain from "../main/second/mainbar/miun/MiunMain";
import { LINKS } from "./config";
import TeudotDetailsMain from "../main/second/mainbar/queries/TeudotDetails/TeudotDetailsMain";
import MarketCollectMain from "../main/second/mainbar/market/Collect/MarketCollectMain";
import MainQ4 from "../main/second/mainbar/procedures/Q4/MainQ4";
import MainQ2 from "../main/second/mainbar/procedures/Q2/MainQ2";
import MainQ8 from "../main/second/mainbar/procedures/Q8/MainQ8";
import PalletLocationMain from "../main/second/mainbar/palletLocation/PalletLocationMain";
import EditCustomerMain from "../main/second/mainbar/Pallets/EditCustomer/EditCustomerMain";
import CoolingSiteMain from "../main/second/mainbar/Pallets/CoolingSite/CoolingSiteMain";
import CustomerBills from "../main/second/mainbar/bills/Customers/CustomerBills";
import PacksMovements from "../main/second/mainbar/arizot/movements/PacksMovements";
import SettingsIndex from "../main/second/mainbar/settings/SettingsIndex";
import NotFound from "../modules/404";
import ReceiptMain from "../main/second/mainbar/receipt/ReceiptMain";
import ReceiptsReportMain from "../main/second/mainbar/bills/ReceiptsReport/ReceiptsReportMain";
import MlaiSectionMain from "../main/second/mainbar/mlai/MlaiSectionMain";
import DriversReportMain from "../main/second/mainbar/trucks/DriverReports/DriversReportMain";
import KnisotExternalMain from "../main/second/mainbar/knisotExternal/KnisotExternalMain";
import MainQ10 from "../main/second/mainbar/procedures/Q10/MainQ10";
import Index from "../main/second/mainbar/Pallets/LikutPrahim/Index";
import OrderCount from "../main/second/mainbar/orderDohs/count/OrderCount";
import SellerMain from "../main/second/mainbar/seller/SellerMain";
import ProductChangesMain from "../main/second/mainbar/products/ProductChanges/ProductChangesMain";
import TableCustomers from "../main/second/mainbar/defaultTables/Customers/TableCustomers";
import SendInvitePage from "../main/second/mainbar/invite/SendInvitePage/SendInvitePage";
import KnisotLinesMain from "../main/second/mainbar/knisotLines/KnisotLinesMain";
import TruckLoading from "../main/second/mainbar/trucks/Loading/TruckLoading";
import ProblemTeudotIndex from "../main/second/mainbar/teudotProblems/ProblemTeudotIndex";
import { ProductLocationIndex } from "../main/second/mainbar/warehouse/location";
import { Reports } from "../main/second/mainbar/teudotProblems/Reports";
import { OrderQuotationMain } from "../main/second/mainbar/orderQuotation";
import { ExpirationReport } from "../main/second/mainbar/warehouse/expiration-report";
import { LocationControl } from "../main/second/mainbar/warehouse/location-control";
import { Warehouse3D } from "../main/second/mainbar/warehouse/warehouse-3d";
import ProductTableModule from "../main/second/mainbar/tabels/product";
import CustomerTableModule from "../main/second/mainbar/tabels/customers";
import GrowerTableModule from "../main/second/mainbar/tabels/growers";
import PackageTableModule from "../main/second/mainbar/tabels/packages";
import PalletTableModule from "../main/second/mainbar/tabels/pallets";
import CarTableModule from "../main/second/mainbar/tabels/cars";

// Descendant <Routes> inside a Route with path="/main/*" match against the
// remainder after the parent base (/main) is stripped. Convert an absolute
// LINKS path like "/main/foo/bar/" into the relative form "foo/bar".
const rel = (absolute?: string) =>
  (absolute ?? "").replace(/^\/main\//, "").replace(/\/$/, "");

// Same as rel() but appends "/*" so the Route also matches every nested URL.
const relSplat = (absolute?: string) => `${rel(absolute)}/*`;

const AppRouting = () => {

  const modules = useSelector(
    (module: RootState) => module.mainReducer.modules
  );

  const redirect = useMemo(() => {
    if (modules) {
      let moduletemp = mainModules(modules[0]?.ModuleId)
      if(!moduletemp) return <Navigate to={"/main/404"} replace/>
      let redirectLink = moduletemp?.subModules[modules[0]?.Sub[0]?.Sub_ModuleID]?.link || moduletemp?.link || '/main/menu1'
      return <Navigate to={redirectLink} replace/>;
    }
  }, [modules]);

  const routerList = useMemo(() => {
    return modules?.map((module, index) => {
      const moduleNum = module.ModuleId;
      const data = mainModules(moduleNum);
      if (module.Sub?.length > 0) {
        // Collect every submodule link so we can suppress Route entries whose
        // link is a sub-path of another submodule's link. In v5 <Switch> always
        // picked the less-specific parent and let it route internally; v7
        // <Routes> picks by specificity and would mount the child component
        // without the parent (losing its provider + fetched props).
        const siblingLinks = module.Sub
          .map((s) => data.subModules[s.Sub_ModuleID]?.link)
          .filter(Boolean) as string[];

        return module.Sub.map((submodule) => {
          const submoduleID = submodule.Sub_ModuleID;
          const subModuleData = data.subModules[submoduleID];
          if (!subModuleData?.link) return null;

          const thisLink = subModuleData.link.replace(/\/$/, "");
          const isNestedInSibling = siblingLinks.some((other) => {
            const otherStripped = other.replace(/\/$/, "");
            return otherStripped !== thisLink && thisLink.startsWith(otherStripped + "/");
          });
          if (isNestedInSibling) return null;

          const SubComponent = subModuleData.Component;
          return (
            <Route
              key={`${index}-${moduleNum}-${submodule.Sub_ModuleID}`}
              path={relSplat(subModuleData.link)}
              element={SubComponent ? <SubComponent /> : null}
            />
          );
        });
      } else {
        const LeafComponent = data?.Component;

        return (
          <Route
            key={index}
            path={data?.link ? relSplat(data.link) : undefined}
            element={LeafComponent ? <LeafComponent /> : null}
          />
        );
      }
    });
  }, [modules]);

  return (
    <Routes>
      {routerList}
      <Route path={rel(LINKS.tables.products.mainURL)} element={<ProductTableModule/>} />
      <Route path={rel(LINKS.tables.customers.mainURL)} element={<CustomerTableModule/>} />
      <Route path={rel(LINKS.tables.growers.mainURL)} element={<GrowerTableModule/>} />
      <Route path={rel(LINKS.tables.packages.mainURL)} element={<PackageTableModule/>} />
      <Route path={rel(LINKS.tables.pallets.mainURL)} element={<PalletTableModule/>} />
      <Route path={rel(LINKS.tables.cars.mainURL)} element={<CarTableModule/>} />
      <Route path={relSplat(LINKS.orders.quotation.mainUrl)} element={<OrderQuotationMain/>} />
      <Route path={rel(LINKS.warehouse.expirationReport.mainURL)} element={<ExpirationReport/>} />
      <Route path={rel(LINKS.warehouse.locationControl.mainURL)} element={<LocationControl/>} />
      <Route path={rel(LINKS.warehouse.warehouse3d.mainURL)} element={<Warehouse3D/>} />
      <Route path={rel(LINKS.teudotProblems.reports.mainUrl)} element={<Reports/>} />
      <Route path="pallet/product-location" element={<ProductLocationIndex/>} />
      <Route path="procedures/q4" element={<MainQ4/>}/>
      <Route path={rel(LINKS.auth.invite.mainURL)} element={<SendInvitePage/>}/>
      <Route path={rel(LINKS.knisot.lines.mainURL)} element={<KnisotLinesMain/>} />
      <Route path={relSplat(LINKS.teudotProblems.list.mainUrl)} element={<ProblemTeudotIndex/>} />
      <Route path={relSplat(LINKS.afaca.loading.mainURL)} element={<TruckLoading/>} />
      <Route path={rel(LINKS.queries.q8.mainURL)} element={<MainQ8/>} />
      <Route path={relSplat(LINKS.mishtahim.likutPrahim.mainURL)} element={<Index/>} />
      <Route path={rel(LINKS.arizot.movements.mainUrl)} element={<PacksMovements/>} />
      <Route path={rel(LINKS.mishtahim.location.mainURL)} element={<PalletLocationMain/>} />
      <Route path="queries/teudot-details" element={<TeudotDetailsMain/>} />
      <Route path={rel(LINKS.mishtahim.editCustomer.mainURL)} element={<EditCustomerMain/>} />
      <Route path={rel(LINKS.mishtahim.coolingSite.mainURL)} element={<CoolingSiteMain/>} />
      <Route path={rel(LINKS.afaca.driversReport.mainURL)} element={<DriversReportMain/>} />
      <Route path="market" element={<MarketCollectMain/>} />
      <Route path={rel(LINKS.bills.customerBills.mainURL)} element={<CustomerBills/>} />
      <Route path={relSplat(LINKS.bills.receipt.mainURL)} element={<ReceiptMain/>} />
      <Route path={rel(LINKS.bills.receiptsReport.mainURL)} element={<ReceiptsReportMain/>} />
      <Route path={relSplat(LINKS.mlai.mlai.mainURL)} element={<MlaiSectionMain/>} />
      <Route path={rel(LINKS.knisot.external.mainURL)} element={<KnisotExternalMain/>} />
      <Route path={rel(LINKS.queries.q10.mainURL)} element={<MainQ10/>} />
      <Route path={rel(LINKS.forecasts.seller.mainURL)} element={<SellerMain/>} />
      <Route path={rel(LINKS.orders.count.mainUrl)} element={<OrderCount/>} />
      <Route path={rel(LINKS.orders.product.mainUrl)} element={<ProductChangesMain/>} />
      <Route path="settings/*" element={<SettingsIndex/>} />
      <Route path="miun/control/*" element={<MiunMain/>} />
      <Route path={rel(LINKS.defaultTables.clients.mainURL)} element={<TableCustomers/>} />
      <Route path="404" element={<NotFound/>} />
      {redirect && <Route path="*" element={redirect} />}
    </Routes>
  );
}

export default React.memo(AppRouting)
